import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "../../../components/ui/button";
import { ExternalLink } from "lucide-react";
import { OfferForm } from "./offer.schema";

/**
 * Preview & send (Phase 1, C1): the EXACT client view — the live offer page
 * itself, reactions and all, framed in the builder. Environment-aware host
 * (staging builder previews the staging site). For unsaved offers there is
 * nothing public to frame yet, so we prompt to save first.
 */
export function Preview({ offerId }: { offerId?: string }) {
  const brand = useFormContext<OfferForm>().getValues().description?.brand;

  const url = useMemo(() => {
    if (!offerId) return null;
    const staging = typeof window !== "undefined" && window.location.hostname.includes("staging");
    const host =
      brand === "anchor"
        ? staging
          ? "https://staging.anchor.yt"
          : "https://www.anchor.yt"
        : staging
          ? "https://staging.helm.yt"
          : "https://www.helm.yt";
    return `${host}/offer/${offerId}`;
  }, [offerId, brand]);

  if (!url) {
    return (
      <Card>
        <h3 className="text-lg font-semibold mb-2">Preview</h3>
        <p className="text-sm text-slate-500">
          Save the offer first — the preview shows the real client page, which exists once the draft is saved.
        </p>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Client preview</h3>
          <p className="text-sm text-slate-500">
            Exactly what the client sees — compare views, reactions and feedback are live in this frame.
          </p>
        </div>
        <Button asChild variant="outline">
          <a href={url} target="_blank" rel="noreferrer">
            Open in new tab <ExternalLink className="ml-2" size={16} />
          </a>
        </Button>
      </div>
      <iframe
        src={url}
        title="Client offer preview"
        className="w-full rounded-lg border border-blue-100"
        style={{ height: "72vh" }}
      />
    </Card>
  );
}
